import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useFormikContext} from "formik";

import FunctionButton from "../../../../../../utilities/Buttons/FunctionButton";
import Divider from "../../../../../../utilities/Divider/Divider";
import {swalCustom} from "../../../../../../utilities/hooks/swalCustom";
import {toaster} from "../../../../../../utilities/hooks/toaster";
import ReactTable from "../../../../../conteiners/ReactTable";
import {MoneyInput, NumberInput, Select, TextInput} from "../../../../../Inputs";
import FormikCheckBoxWithLabel from "../../../../../Inputs/FormikCheckBoxWithLabel/FormikCheckBoxWithLabel";
import useColumns from "../../../../../Requests/OrderProcessing/utils/hooks/useColumns";
import {
  addNewCOfService,
  deleteCOfServices,
  fetchAllCOfDiscounts,
  fetchAllCOfServices,
  fetchCOf,
  fetchCOfServiceTypes,
  selectAllCOfServices,
  selectCOfServiceTypes,
  selectCurrentCof,
  updateCOf,
} from "../../../KpFinSlice";
import {SERVICE_TABLE_COLUMNS} from "../../utils";

const ServicePart = ({setIsSaved, kpReadOnly}) => {
  const {
    t,
    i18n: {language},
  } = useTranslation();
  const dispatch = useDispatch();
  const {values, setValues, setFieldValue} = useFormikContext();

  const {serviceTypeId, serviceCost} = values;
  const noServiceInputValues = !serviceTypeId || !serviceCost;

  const currentCOf = useSelector(selectCurrentCof);
  const cofId = currentCOf?.cofId;
  const advanceForServices = currentCOf?.advanceForServices;

  useEffect(() => {
    if (currentCOf) setFieldValue("isAdvanceForWholeCof", advanceForServices);
  }, [advanceForServices]);

  useEffect(() => {
    dispatch(fetchAllCOfServices({cofId}));
    dispatch(fetchCOfServiceTypes());
  }, []);

  const services = useSelector(selectAllCOfServices);
  const noServices = !services || services.length === 0;
  const serviceTypes = useSelector(selectCOfServiceTypes);

  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const areServicesSelected = selectedServiceIds?.length > 0;

  const serviceTypeOptions =
    serviceTypes?.map(({typeId, nameEng, nameUkr}) => ({
      value: typeId,
      title: language === "en" ? nameEng : nameUkr,
    })) ?? [];

  const serviceTableColumns = useColumns(SERVICE_TABLE_COLUMNS);
  const serviceTableData =
    services?.map(({serviceId, serviceCost, type: {nameEng, nameUkr} = {}}, index) => ({
      id: serviceId,
      number: index + 1,
      service: language === "en" ? nameEng : nameUkr,
      serviceCost,
    })) ?? [];

  const onAddServiceBtnClick = () => {
    dispatch(addNewCOfService({cofId, typeId: serviceTypeId, serviceCost})).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        toaster.success(t("Service added successfully"));
        dispatch(fetchAllCOfServices({cofId}));
        dispatch(fetchAllCOfDiscounts({cofId}));
        setValues(prev => ({...prev, serviceTypeId: "", serviceCost: ""}));
        setIsSaved(false);
        dispatch(fetchCOf({cofId}));
      }
    });
  };
  const onDeleteServiceBtnClick = () => {
    swalCustom.confirm({
      confirmFunc: () => {
        dispatch(deleteCOfServices({serviceIds: selectedServiceIds})).then(resp => {
          if (resp.meta.requestStatus === "fulfilled") {
            toaster.success(t("Service deleted successfully"));
            setValues(prev => ({...prev, serviceTypeId: "", serviceCost: ""}));
            setIsSaved(false);
            dispatch(fetchAllCOfDiscounts({cofId}));
            dispatch(fetchCOf({cofId}));
          }
        });
      },
    });
  };

  const onMultiSelect = selectedRows => {
    setSelectedServiceIds(selectedRows ?? []);
  };

  const onAdvanceCheckBoxChange = () => {
    dispatch(updateCOf({cofId, body: {advanceForServices: values?.isAdvanceForWholeCof}})).then(resp => {
      if (resp.meta.requestStatus === "fulfilled") {
        dispatch(fetchAllCOfDiscounts({cofId}));
        setIsSaved(false);
        dispatch(fetchCOf({cofId}));
      }
    });
  };

  useEffect(() => {
    if (
      values?.isAdvanceForWholeCof !== undefined &&
      currentCOf &&
      advanceForServices !== values?.isAdvanceForWholeCof
    ) {
      onAdvanceCheckBoxChange();
    }
  }, [values?.isAdvanceForWholeCof, currentCOf, advanceForServices]);

  return (
    <div className="fdr gap">
      <div className="fcCols gap5">
        <div className="fdr blur" style={{gap: "5.5em"}}>
          <TextInput label="Product country" name="country" labelStyles={{width: "9em"}} width="13em" disabled />
          <TextInput label="Delivery term" name="deliveryTerm" labelStyles={{width: "9em"}} width="13em" disabled />
        </div>
        <div className="fdr gap blur">
          <NumberInput label="~∑ (m3)" name="fieldM3" width="8.8em" disabled />
          <NumberInput label="~∑ (kg)" name="fieldKg" width="8.8em" disabled />
          <MoneyInput label="~∑ (€)" name="fieldEvr" width="8.8em" disabled />
        </div>
        <div className="row jcfe w100 mb0_5 mt5">
          <FunctionButton name="Save" disabled />
        </div>
        <div className="fdr gap w100">
          <div className="fdr gap aic fGrow">
            {/* <Field name="" type="checkbox" checked={isCheckedService} onChange={handleServiceChange} /> */}
            <Select
              label="Service"
              name="serviceTypeId"
              labelStyles={{width: "17em"}}
              options={serviceTypeOptions}
              generalClassName="fGrow"
              disabled={areServicesSelected || kpReadOnly}
            />
          </div>
          <MoneyInput label="Sum" name="serviceCost" width="8em" disabled={areServicesSelected || kpReadOnly} />
        </div>
        <div className="row jcfe gap3x w100 mb0_5 mt5">
          <FunctionButton
            name="Delete"
            disabled={!areServicesSelected || kpReadOnly}
            onClick={onDeleteServiceBtnClick}
          />
          <FunctionButton
            name="Add"
            tooltipMessage={
              !noServiceInputValues && !areServicesSelected ? "Select service type and enter service sum" : ""
            }
            onClick={onAddServiceBtnClick}
            disabled={areServicesSelected || noServiceInputValues || kpReadOnly}
          />
        </div>
        <Divider margin="5px" />
        <div className="row w100 jcfs gap3x">
          <FormikCheckBoxWithLabel
            name="isAdvanceForWholeCof"
            label="70% to Co"
            disabled={noServices || kpReadOnly}
            tooltipMessage={noServices ? "No services added" : ""}
          />
          {/* <FunctionButton
            name={t("Calculate")}
            disabled={isAdvanceForWholeCof === values?.isAdvanceForWholeCof}
            onClick={setAdvanceForWholeCof(isAdvanceForWholeCof)}
          /> */}
        </div>
      </div>
      <div className="fGrow" style={{width: "49%"}}>
        <ReactTable
          columns={serviceTableColumns}
          defaultData={serviceTableData}
          onSelect={() => {}}
          enableMultiSelection={kpReadOnly ? false : true}
          className="fGrow scrollX scrollY fShrink"
          tableClassName="fGrow"
          selectable={false}
          setMultiSelect={onMultiSelect}
        />
      </div>
    </div>
  );
};

export default ServicePart;
